import React from 'react';
import { Metafields, Product, ProductTypes } from '@/types/ecommerce.types';
import { withQuery } from '../data/withQuery';
import {
  getCurrentVariant,
  getMetafieldV2,
  isOrnamentProduct,
} from '@/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { resetGalleryItem } from '@/store/product/productSlice';
import { setShouldCropToAll } from '@/store/upload/uploadSlice';
import { navigate } from '@reach/router';
import { MatWrapper, ProductOptionsTitle } from './ProductOptions.styles';
import { getSortDictionary } from './getSortDictionary';
import { getPartner } from '@/utils/partner';
import PriceDisplay from '../shared/PriceDisplay';
import { ProductVariantEdge } from 'shopify-storefront-api-typings';

type Variant = {
  id: string;
  handle: string;
  mat_group_title?: string | null;
  mat_type_label?: string | null;
  variant?: ProductVariantEdge;
};

type Node = {
  id: string;
  handle: string;
  mat_group_title?: { value: string };
  mat_type_label?: { value: string };
  mat_type?: { value: string };
  variants?: {
    edges: ProductVariantEdge[];
  };
};

type PropsType = {
  product: Product;
  shopMetafields: Metafields;
};

const MatOptions = ({ product, shopMetafields }: PropsType) => {
  const dispatch = useDispatch();
  const partner = getPartner();
  const tile = useSelector((state: RootState) => state.upload.tiles[0]);
  const { type } = useSelector((state: RootState) => state.product);
  const isGallery = type === ProductTypes.GALLERY;

  if (!product) return null;

  const currentVariantFromPageProduct = getCurrentVariant(product?.variants?.edges, null, {
    checkParams: true,
  });
  const frameSize = currentVariantFromPageProduct?.selectedOptions[0]?.value;
  const currentProductMatType = getMetafieldV2('mat_type_label', product.metafields);
  const matGroupTitle = getMetafieldV2('mat_group_title', product.metafields);
  const matTitleDescription = getMetafieldV2('mat_title_description', product.metafields);
  let matProductGroup: Variant[] = [];

  // TODO MEM-1247 remove after updates START
  const currentProduct = getMetafieldV2('mat_type', product.metafields);
  const variants = product?.mat_variants?.references?.edges.map(
    (edge: { node: Node }) => edge?.node
  );
  if (variants?.length) {
    matProductGroup = variants?.map((name: Node) => ({
      id: name?.id,
      handle: name?.handle,
      mat_type_label: name?.mat_type?.value,
      variant: name?.variants?.edges?.[0],
    }));
    const sort = getSortDictionary(getMetafieldV2('mat_toggle_order', shopMetafields));
    if (currentProduct && matProductGroup) {
      matProductGroup.push({
        id: product.id,
        handle: product.handle,
        mat_type_label: currentProduct,
        variant: product?.variants?.edges?.[0],
      });
    }
    matProductGroup.sort(
      (a, b) => sort[a?.mat_type_label?.toLowerCase()] - sort[b?.mat_type_label?.toLowerCase()]
    );
  }
  // TODO MEM-1247 remove after updates END

  if (product.mat_product_group?.references?.edges?.length) {
    // TODO MEM-1247 remove condition
    matProductGroup = product.mat_product_group?.references?.edges.map((el: { node: Node }) => ({
      id: el.node.id,
      handle: el.node.handle,
      mat_group_title: el.node.mat_group_title?.value,
      mat_type_label: el.node.mat_type_label?.value,
      variant: el.node.variants?.edges?.[0],
    }));

    if (!matProductGroup.some((el: Variant) => el.id === product.id)) {
      matProductGroup.unshift({
        id: product.id,
        handle: product.handle,
        mat_group_title: matGroupTitle,
        mat_type_label: currentProductMatType,
        variant: product?.variants?.edges?.[0],
      });
    }
  }

  if (!matProductGroup?.length) {
    return null;
  }

  return (
    <MatWrapper>
      <ProductOptionsTitle>
        {matGroupTitle || 'Mat'}:
        <span>{matTitleDescription || currentProductMatType || currentProduct}</span>
      </ProductOptionsTitle>
      <ul className="tmplt-product__variants-list">
        {matProductGroup.map(({ handle, mat_type_label, variant }) => (
          <li
            className="typo__paragraph--small tmplt-product__variants-item tmplt-product__variants-item--"
            key={handle}
            onClick={() => {
              if (!handle) return;
              dispatch(resetGalleryItem());
              if (tile) {
                dispatch(setShouldCropToAll());
              }

              const searchParams = new URLSearchParams();
              searchParams.set('size', frameSize as string);
              if (partner) {
                searchParams.set('partner', partner);
              }

              const url = `/products/${handle}?${searchParams.toString()}`;

              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => {
                navigate(url);
              }, 250);
            }}
          >
            {handle === product.handle && (
              <input
                type="radio"
                checked={true}
                onChange={() => {}}
                className="fn-variant-input tmplt-product__variants-input visually-hidden"
              />
            )}
            <label className="tmplt-product__variants-label">
              <span>{mat_type_label}</span>
              {isOrnamentProduct(type, product?.title) && isGallery && (
                <PriceDisplay product={product} variant={variant} />
              )}
            </label>
          </li>
        ))}
      </ul>
    </MatWrapper>
  );
};

export default withQuery(MatOptions);
